import type {
  DataGridCellRenderer,
  DataGridCellRendererProps,
  DataGridRef,
  JsonData,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataSender,
  RowTemplateType,
} from '@myosh/odin-components';
import {
  Button,
  DataGrid,
  DataSearchLocation,
  type DynamicFormFieldType,
  EmptyFieldLabel,
  ErrorLabel,
  FieldLabel,
  RequiredIndicator,
} from '@myosh/odin-components';
import clsx from 'clsx';
import type { Ref } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LinkedUser } from '../../../@types/users';
import useActiveRecordRef from '../../../hooks/use-active-record-ref';
import useFetchLinkedUsers from '../../../hooks/use-fetch-linked-users';
import useFetchUsers from '../../../hooks/use-fetch-users';
import { useUserAccess } from '../../../hooks/use-user-access';
import type { UserRecordLinkProps } from '../../../utils/linked-user-utils';
import { extractUserIds, transformUserRecordLinkColumns } from '../../../utils/linked-user-utils';
import { forceAssert } from '../../../utils/type-guards';
import { UserFields } from '../../../utils/user-config';
import { UsersPermissions } from '../../../utils/user.permissions';
import { OdinDataGridLinkedUsersRowTemplate } from '../../dynamic-page/odin-data-grid-linked-users-row-template.component';
import LinkedRecordsActionsCell from '../linked-records/components/linked-records-action-cell';
import CreateUserDialog from './components/create-user-dialog';
import UserRecordLinkPropertyNameCell from './components/user-record-link-cell.component';
import UserRecordLinkSelecting from './components/user-record-link-selecting';

export const linkedUsersCustomComponents: Record<string, DataGridCellRenderer> = {
  USER_FIELD: {
    CellComponent: (props: DataGridCellRendererProps, ref: Ref<HTMLDivElement>) => (
      <UserRecordLinkPropertyNameCell {...props} ref={ref} />
    ),
  },
};

export default function UserRecordLink({
  label,
  readOnly,
  required,
  value,
  onChange,
  recordLinkFormat,
  buttonCaption,
  showSelectButton,
  createExternalUsers,
  saveRecord,
  error,
}: UserRecordLinkProps) {
  const [linkedUsers, setLinkedUsers] = useState<Array<LinkedUser>>(value || []);
  const [linkedUsersIds, setLinkedUsersIds] = useState<Array<number>>([]);
  const [linkedUsersData, setLinkedUsersData] = useState<JsonData>([]);
  const [selectLinkedUsersDialogVisible, setSelectLinkedUsersDialogVisible] = useState<boolean>(false);
  const [createUserDialogVisible, setCreateUserDialogVisible] = useState<boolean>(false);
  const [gridOptions, setGridOptions] = useState<OdinDataRetrievalOptions>();

  const gridSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const gridRef = useRef<DataGridRef | undefined>(undefined);

  const { t } = useTranslation();
  const { activeRecordReference } = useActiveRecordRef();

  const userFields = useMemo(() => {
    return (
      recordLinkFormat
        ?.map((column) => column.entityPropertyName)
        .concat(UserFields.userHierarchy)
        .join() || ''
    );
  }, [recordLinkFormat]);

  const { linkedUsersRecords, isFetching: areLinkedUserRecordsFetching } = useFetchLinkedUsers(
    userFields,
    gridOptions?.page ?? 1,
    gridOptions?.pageSize ?? 50,
    linkedUsersIds,
    gridOptions?.sortedFields
  );

  const { users } = useFetchUsers(linkedUsersIds, userFields);

  useEffect(() => {
    setLinkedUsers(value ?? []);
    resetGridPageData();
  }, [value]);

  useEffect(() => {
    let data: JsonData = [];
    if (linkedUsersRecords && linkedUsersRecords.length > 0) {
      data = forceAssert<JsonData>(linkedUsersRecords);
    }
    setLinkedUsersData(data);
  }, [linkedUsersRecords]);

  useEffect(() => {
    const userIds = extractUserIds(linkedUsers);
    setLinkedUsersIds(userIds);
  }, [linkedUsers]);

  const gridColumns = useMemo(() => {
    const userRecordLinkColumns = recordLinkFormat ? transformUserRecordLinkColumns(recordLinkFormat) : [];
    userRecordLinkColumns.push({
      id: 'linkedUsersRemoveAction',
      field: '',
      title: '',
      visible: !readOnly,
      cellRenderer: 'LINKED_RECORDS_ACTIONS',
      type: 6,
      disableSort: true,
    });
    return userRecordLinkColumns;
  }, [recordLinkFormat]);

  const gridSettings = {
    columns: gridColumns,
    autoSizeColumns: true,
    components: {
      ...linkedUsersCustomComponents,
      LINKED_RECORDS_ACTIONS: {
        CellComponent: (props: DataGridCellRendererProps) => {
          return <LinkedRecordsActionsCell rowData={props.rowData} removeLinkedRecord={handleRemoveLinkedRecord} />;
        },
      },
    },
    filterLocation: DataSearchLocation.Api,
    stopRequestsOnNoData: true,
    fullHeight: true,
  };

  const handleRemoveLinkedRecord = useCallback(
    (rowId: string) => {
      const filteredList: LinkedUser[] = linkedUsers?.filter((user) => String(user.value) !== rowId) ?? [];
      onChange?.(filteredList);
      saveRecord?.(); // save the Record when a linked record is removed (MYOSH-5074)
      resetGridPageData();
    },
    [onChange, saveRecord]
  );

  const resetGridPageData = () => gridRef.current?.api.data.getDataOverwritePageCache();

  const rowTemplate: RowTemplateType = OdinDataGridLinkedUsersRowTemplate({
    activeRecordReference: activeRecordReference,
    successCallback: resetGridPageData,
  });

  const onSelectLinkedUsersDialogOpen = () => setSelectLinkedUsersDialogVisible(true);
  const onSelectLinkedUsersDialogHidden = () => setSelectLinkedUsersDialogVisible(false);

  const onCreateUserDialogOpen = () => setCreateUserDialogVisible(true);
  const onCreateUserDialogHidden = () => setCreateUserDialogVisible(false);

  const handleUserCreated = (user: LinkedUser) => {
    handleAddLinkedUsers([...linkedUsers, user]);
  };

  const handleAddLinkedUsers = (addedLinkedUsers: Array<LinkedUser>) => {
    setLinkedUsers(addedLinkedUsers);
    onChange?.(addedLinkedUsers);
    saveRecord?.(); // save the Record when a linked user is selected or created
    resetGridPageData();
  };

  const { createAccess: canCreateNewUser } = useUserAccess({
    permissions: {
      CREATE: [UsersPermissions.USERS_CREATE],
    },
  });

  useEffect(() => {
    if (linkedUsersData && !areLinkedUserRecordsFetching) {
      if (linkedUsersData.length > 0) {
        gridSubscriber.current?.sendData({ data: linkedUsersData, requestId: gridOptions?.requestId });
      } else {
        gridSubscriber.current?.sendData();
      }
    }
  }, [linkedUsersData, gridOptions]);

  const gridData = useMemo<OdinDataRetrieval>(() => {
    return {
      getSubscriber: (subscriber, fieldType?: DynamicFormFieldType) => {
        if (fieldType !== 'COMBOBOX') {
          gridSubscriber.current = subscriber;
        }
      },
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options?.fieldType !== 'COMBOBOX') {
          setGridOptions(options);
        } else {
          subscriber.sendData();
        }
      },
    };
  }, []);

  const onDataGridRefCreated = (dataGridRef: DataGridRef) => {
    gridRef.current = dataGridRef;
  };

  const buttonsContainerStyles = clsx('my-2 flex flex-row m-1 gap-2', {
    'border border-error': error && !readOnly,
  });

  return (
    <>
      {label && (
        <div className="flex">
          <FieldLabel label={label} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {!readOnly && (
        <div className={buttonsContainerStyles}>
          {canCreateNewUser && (
            <Button type="primary" variant="alternative" onClick={onCreateUserDialogOpen}>
              {buttonCaption || t('create-user')}
            </Button>
          )}
          {showSelectButton && (
            <Button variant="alternative" classNames="!bg-mono-1" onClick={onSelectLinkedUsersDialogOpen}>
              {t('select')}
            </Button>
          )}
        </div>
      )}
      {readOnly && linkedUsers.length === 0 && <EmptyFieldLabel />}
      {users.length > 0 && (
        <div className="my-2 flex max-h-[40rem]">
          <DataGrid
            ref={(gridRef) => {
              if (gridRef) onDataGridRefCreated(gridRef);
            }}
            data={gridData}
            gridSettings={gridSettings}
            showSettings={false}
            rowTemplate={rowTemplate}
          />
        </div>
      )}
      {selectLinkedUsersDialogVisible && (
        <UserRecordLinkSelecting
          visible={selectLinkedUsersDialogVisible}
          hidden={onSelectLinkedUsersDialogHidden}
          value={linkedUsers}
          applyCallback={handleAddLinkedUsers}
        />
      )}
      {createUserDialogVisible && (
        <CreateUserDialog
          header={t('new-user')}
          visible={createUserDialogVisible}
          hidden={onCreateUserDialogHidden}
          onUserCreated={handleUserCreated}
          createExternalUser={createExternalUsers}
        />
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
}
